import { CADENA_VACIA } from 'utils/constants';
import { parseFlexibleDate } from 'utils/formatters/dateFormatter';

/**
 * Campo título/texto ya parseado de la descripción del KML.
 */
export type KmlCampo = { titulo?: string; texto?: string };

/**
 * Datos de corte que se pueden leer del polígono cuando no hay tracking.
 */
export type KmlOutageFields = {
  /**
   * Fecha de reposición o restauración estimada leída del KML
   */
  terFecha: Date | null;
  /**
   * Hay una fecha de TER interpretable
   */
  tieneTer: boolean;
  /**
   * Cantidad de clientes afectados del campo KML
   */
  clientesSinServicio: number | null;
  /**
   * Comuna del polígono; vacía si el campo no viene
   */
  comuna: string;
};

/**
 * Título de reposición estimada con tilde.
 */
const TITULO_REPOSICION_ESTIMADA = 'reposición estimada';

/**
 * Título de reposición estimada sin tilde.
 */
const TITULO_REPOSICION_SIN_TILDE = 'reposicion estimada';

/**
 * Título de restauración estimada con tilde.
 */
const TITULO_RESTAURACION_ESTIMADA = 'restauración estimada';

/**
 * Título de restauración estimada sin tilde.
 */
const TITULO_RESTAURACION_SIN_TILDE = 'restauracion estimada';

/**
 * Fragmento del título de clientes afectados.
 */
const TITULO_CLIENTES_AFECTADOS = 'clientes afectados';

/**
 * Título del campo comuna.
 */
const TITULO_COMUNA = 'comuna';

/**
 * Normaliza un título de campo KML para compararlo sin espacios ni mayúsculas.
 * @param valor título del campo
 * @returns título en minúsculas y sin espacios laterales
 */
const normalizar = (valor?: string): string => (valor || CADENA_VACIA).trim().toLowerCase();

/**
 * Indica si el campo corresponde a la reposición o restauración estimada.
 * @param titulo título ya normalizado
 * @returns true si es el campo de TER
 */
const esCampoTer = (titulo: string): boolean =>
  titulo.includes(TITULO_REPOSICION_ESTIMADA) ||
  titulo.includes(TITULO_REPOSICION_SIN_TILDE) ||
  titulo.includes(TITULO_RESTAURACION_ESTIMADA) ||
  titulo.includes(TITULO_RESTAURACION_SIN_TILDE);

/**
 * Indica si el campo corresponde a clientes afectados.
 * @param titulo título ya normalizado
 * @returns true si es el campo de clientes
 */
const esCampoClientes = (titulo: string): boolean => titulo.includes(TITULO_CLIENTES_AFECTADOS);

/**
 * Indica si el campo corresponde a la comuna del polígono.
 * @param titulo título ya normalizado
 * @returns true si el título es "comuna"
 */
const esCampoComuna = (titulo: string): boolean => titulo === TITULO_COMUNA;

/**
 * Extrae la cantidad de clientes de un texto del KML.
 * @param texto valor del campo, por ejemplo "1.234 clientes"
 * @returns número entero o null si no hay dígitos
 */
const parseClientes = (texto?: string): number | null => {
  if (!texto) {
    return null;
  }
  const digits = texto.replace(/[^\d]/g, CADENA_VACIA);
  if (digits === CADENA_VACIA) {
    return null;
  }
  const n = Number.parseInt(digits, 10);
  return Number.isNaN(n) ? null : n;
};

/**
 * Extrae TER, clientes sin servicio y comuna de los campos ya parseados del KML.
 * @param campos filas título/texto de la descripción del polígono
 * @returns fechas, clientes y comuna; vacíos si el campo no viene
 */
export const parseKmlOutageFields = (campos?: KmlCampo[] | null): KmlOutageFields => {
  const rows = Array.isArray(campos) ? campos : [];
  const terRow = rows.find(row => esCampoTer(normalizar(row.titulo)));
  const clientesRow = rows.find(row => esCampoClientes(normalizar(row.titulo)));
  const comunaRow = rows.find(row => esCampoComuna(normalizar(row.titulo)));
  const terFecha = parseFlexibleDate(terRow?.texto);
  const clientesSinServicio = parseClientes(clientesRow?.texto);

  return {
    terFecha,
    tieneTer: terFecha !== null,
    clientesSinServicio,
    comuna: (comunaRow?.texto || CADENA_VACIA).trim(),
  };
};

/**
 * Obtiene la comuna del polígono desde el campo KML "Comuna".
 * @param campos filas título/texto de la descripción del polígono
 * @returns comuna o cadena vacía si no viene
 */
export const extraerComunaKml = (campos?: KmlCampo[] | null): string => parseKmlOutageFields(campos).comuna;
